import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Bell, CalendarDays, ClipboardList, Download, Home, LogOut, RefreshCw, Search, Wrench } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { resolveBackendBaseUrl } from '../utils/runtimeConfig';
import StaffPageHeader from '../components/StaffPageHeader';
import { getStoredStaffRole, getStaffHomeRoute } from '../utils/staffAuth';
import { clearStaffSession, requestStaffLogout } from '../utils/runtimeConfig';
import { AppShell, PageContainer } from '../features/ui-system';

const API = `${resolveBackendBaseUrl()}/api`;
const BASE_URL = `${resolveBackendBaseUrl()}`.replace(/\/$/, '');
const doneStatuses = new Set(['completed', 'needs_special_handling']);

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('id-ID');
};

const formatNumber = (value, digits = 0) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  return number.toLocaleString('id-ID', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const toDateString = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const getHeaders = () => {
  const token = localStorage.getItem('ktc_token');
  return { Authorization: `Bearer ${token}` };
};

const getTicketStatusLabel = (status) => {
  if (status === 'pending_approval') return 'Menunggu Approval';
  if (status === 'approved') return 'Turun Lapangan';
  if (status === 'completed') return 'Selesai';
  if (status === 'needs_special_handling') return 'Butuh Khusus';
  return 'Tidak diketahui';
};

const getTicketStatusClass = (status) => {
  if (status === 'completed') return 'text-green-300 border-green-500/30 bg-green-500/10';
  if (status === 'needs_special_handling') return 'text-red-300 border-red-500/30 bg-red-500/10';
  if (status === 'approved') return 'text-cyan-300 border-cyan-500/30 bg-cyan-500/10';
  if (status === 'pending_approval') return 'text-yellow-300 border-yellow-500/30 bg-yellow-500/10';
  return 'text-gray-300 border-white/10 bg-white/5';
};

const getNotificationClass = (level) => {
  if (level === 'error') return 'border-red-500/30 bg-red-500/10 text-red-200';
  if (level === 'warning') return 'border-yellow-500/30 bg-yellow-500/10 text-yellow-100';
  if (level === 'success') return 'border-green-500/30 bg-green-500/10 text-green-100';
  return 'border-cyan-500/20 bg-cyan-500/10 text-cyan-100';
};

const ticketBelongsToTechnician = (ticket, row) => {
  if (!ticket || !row) return false;
  const techId = String(row.technician_id || '').trim();
  const techName = String(row.technician_name || '').trim().toLowerCase();
  const approvedIds = Array.isArray(ticket.approved_technician_ids) ? ticket.approved_technician_ids : [];
  const completedIds = Array.isArray(ticket.completed_technician_ids) ? ticket.completed_technician_ids : [];
  const approvedNames = Array.isArray(ticket.approved_technician_names) ? ticket.approved_technician_names : [];
  const completedNames = Array.isArray(ticket.completed_technician_names) ? ticket.completed_technician_names : [];

  return approvedIds.includes(techId)
    || completedIds.includes(techId)
    || approvedNames.some((name) => String(name || '').trim().toLowerCase() === techName)
    || completedNames.some((name) => String(name || '').trim().toLowerCase() === techName);
};

const TechnicianPerformancePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [role, setRole] = useState('admin');
  const [tickets, setTickets] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [periodFrom, setPeriodFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [periodTo, setPeriodTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [statusFilter, setStatusFilter] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState('');
  const [selectedTechnicianKey, setSelectedTechnicianKey] = useState('');
  const [markingNotifications, setMarkingNotifications] = useState(false);
  const [rankingPage, setRankingPage] = useState(1);
  const [rankingPerPage, setRankingPerPage] = useState(20);
  const [detailPage, setDetailPage] = useState(1);
  const [detailPerPage, setDetailPerPage] = useState(12);

  useEffect(() => {
    const storedUser = localStorage.getItem('ktc_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        setUser(null);
      }
    }
    setRole(getStoredStaffRole());
  }, []);

  const fetchData = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);

    try {
      const headers = { headers: getHeaders() };
      const [ticketRes, technicianRes, activityRes, notificationRes] = await Promise.all([
        axios.get(`${API}/field-tickets`, headers),
        axios.get(`${API}/users`, { ...headers, params: { role: 'technician' } }),
        axios.get(`${API}/activity-logs`, { ...headers, params: { limit: 12 } }),
        axios.get(`${API}/notifications`, { ...headers, params: { limit: 8 } }),
      ]);

      setTickets(Array.isArray(ticketRes.data) ? ticketRes.data : []);
      setTechnicians(Array.isArray(technicianRes.data) ? technicianRes.data : []);
      setActivityLogs(Array.isArray(activityRes.data?.logs) ? activityRes.data.logs : []);
      setNotifications(Array.isArray(notificationRes.data?.notifications) ? notificationRes.data.notifications : []);
      setLastUpdated(new Date().toISOString());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memuat performa teknisi');
      setTickets([]);
      setTechnicians([]);
      setActivityLogs([]);
      setNotifications([]);
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = setInterval(() => {
      fetchData(false);
    }, 30000);
    return () => clearInterval(timer);
  }, [autoRefresh, fetchData]);

  const filteredTickets = useMemo(() => {
    const fromDate = periodFrom ? new Date(`${periodFrom}T00:00:00`) : null;
    const toDate = periodTo ? new Date(`${periodTo}T23:59:59`) : null;
    const keyword = String(query || '').trim().toLowerCase();

    return tickets.filter((ticket) => {
      const ticketDateRaw = ticket.completed_at || ticket.updated_at || ticket.created_at;
      const ticketDate = ticketDateRaw ? new Date(ticketDateRaw) : null;
      if (fromDate && ticketDate && ticketDate < fromDate) return false;
      if (toDate && ticketDate && ticketDate > toDate) return false;
      if (statusFilter === 'open' && doneStatuses.has(ticket.status)) return false;
      if (statusFilter === 'done' && !doneStatuses.has(ticket.status)) return false;
      if (statusFilter === 'special' && !(ticket.status === 'needs_special_handling' || ticket.needs_special_handling)) return false;
      if (!keyword) return true;

      const haystack = [
        ticket.customer_name,
        ticket.customer_code,
        ticket.customer_phone,
        ticket.issue_notes,
        ...(Array.isArray(ticket.approved_technician_names) ? ticket.approved_technician_names : []),
        ...(Array.isArray(ticket.completed_technician_names) ? ticket.completed_technician_names : []),
      ].map((item) => String(item || '').toLowerCase()).join(' ');

      return haystack.includes(keyword);
    });
  }, [tickets, periodFrom, periodTo, statusFilter, query]);

  const performanceRows = useMemo(() => {
    const mapped = {};

    const ensureTech = (id, name) => {
      const keyId = String(id || '').trim();
      const keyName = String(name || '').trim();
      const key = keyId || `name:${keyName.toLowerCase()}`;
      if (!mapped[key]) {
        mapped[key] = {
          key,
          technician_id: keyId,
          technician_name: keyName || 'Tanpa Nama',
          approved_count: 0,
          completed_count: 0,
          special_count: 0,
          active_count: 0,
          total_resolution_minutes: 0,
          resolution_samples: 0,
          last_activity_at: '',
          related_customers: new Set(),
        };
      }
      return mapped[key];
    };

    technicians.forEach((technician) => {
      ensureTech(technician.id, technician.name || technician.username);
    });

    filteredTickets.forEach((ticket) => {
      const approvedIds = Array.isArray(ticket.approved_technician_ids) ? ticket.approved_technician_ids : [];
      const approvedNames = Array.isArray(ticket.approved_technician_names) ? ticket.approved_technician_names : [];
      const completedIds = Array.isArray(ticket.completed_technician_ids) ? ticket.completed_technician_ids : [];
      const completedNames = Array.isArray(ticket.completed_technician_names) ? ticket.completed_technician_names : [];

      approvedNames.forEach((name, index) => {
        const row = ensureTech(approvedIds[index], name);
        row.approved_count += 1;
        if (!doneStatuses.has(ticket.status)) row.active_count += 1;
        if (ticket.customer_name) row.related_customers.add(ticket.customer_name);
        const activity = ticket.updated_at || ticket.created_at || '';
        if (activity && (!row.last_activity_at || activity > row.last_activity_at)) row.last_activity_at = activity;
      });

      completedNames.forEach((name, index) => {
        const row = ensureTech(completedIds[index], name);
        row.completed_count += 1;
        if (ticket.status === 'needs_special_handling' || ticket.needs_special_handling) row.special_count += 1;
        if (ticket.customer_name) row.related_customers.add(ticket.customer_name);

        const createdAt = ticket.created_at ? new Date(ticket.created_at) : null;
        const completedAt = ticket.completed_at ? new Date(ticket.completed_at) : null;
        if (createdAt && completedAt && !Number.isNaN(createdAt.getTime()) && !Number.isNaN(completedAt.getTime())) {
          const minutes = Math.max(0, Math.round((completedAt.getTime() - createdAt.getTime()) / 60000));
          row.total_resolution_minutes += minutes;
          row.resolution_samples += 1;
        }

        const activity = ticket.completed_at || ticket.updated_at || ticket.created_at || '';
        if (activity && (!row.last_activity_at || activity > row.last_activity_at)) row.last_activity_at = activity;
      });
    });

    return Object.values(mapped)
      .map((row) => {
        const completionRate = row.approved_count > 0
          ? Math.round((row.completed_count / row.approved_count) * 100)
          : (row.completed_count > 0 ? 100 : 0);
        const avgResolutionHours = row.resolution_samples > 0
          ? (row.total_resolution_minutes / row.resolution_samples) / 60
          : null;

        return {
          ...row,
          customer_count: row.related_customers.size,
          completion_rate: completionRate,
          avg_resolution_hours: avgResolutionHours,
        };
      })
      .sort((a, b) => {
        if (b.completed_count !== a.completed_count) return b.completed_count - a.completed_count;
        if (b.approved_count !== a.approved_count) return b.approved_count - a.approved_count;
        return a.technician_name.localeCompare(b.technician_name);
      });
  }, [filteredTickets, technicians]);

  useEffect(() => {
    if (performanceRows.length === 0) {
      setSelectedTechnicianKey('');
      return;
    }
    if (!performanceRows.some((row) => row.key === selectedTechnicianKey)) {
      setSelectedTechnicianKey(performanceRows[0].key);
    }
  }, [performanceRows, selectedTechnicianKey]);

  const summary = useMemo(() => {
    const openCount = filteredTickets.filter((ticket) => !doneStatuses.has(ticket.status)).length;
    const doneCount = filteredTickets.filter((ticket) => doneStatuses.has(ticket.status)).length;
    const specialCount = filteredTickets.filter((ticket) => ticket.status === 'needs_special_handling' || ticket.needs_special_handling).length;
    const activeTechCount = performanceRows.filter((row) => row.approved_count > 0 || row.completed_count > 0).length;

    return {
      total_tickets: filteredTickets.length,
      open_tickets: openCount,
      done_tickets: doneCount,
      special_tickets: specialCount,
      active_technicians: activeTechCount,
    };
  }, [filteredTickets, performanceRows]);

  const selectedTechnician = useMemo(
    () => performanceRows.find((row) => row.key === selectedTechnicianKey) || null,
    [performanceRows, selectedTechnicianKey]
  );

  const selectedTechnicianTickets = useMemo(() => {
    if (!selectedTechnician) return [];
    return filteredTickets
      .filter((ticket) => ticketBelongsToTechnician(ticket, selectedTechnician))
      .sort((a, b) => String(b.completed_at || b.updated_at || b.created_at || '').localeCompare(String(a.completed_at || a.updated_at || a.created_at || '')));
  }, [filteredTickets, selectedTechnician]);
  const totalRankingPages = Math.max(1, Math.ceil(performanceRows.length / rankingPerPage));
  const pagedPerformanceRows = useMemo(() => {
    const start = (rankingPage - 1) * rankingPerPage;
    return performanceRows.slice(start, start + rankingPerPage);
  }, [performanceRows, rankingPage, rankingPerPage]);
  const totalDetailPages = Math.max(1, Math.ceil(selectedTechnicianTickets.length / detailPerPage));
  const pagedSelectedTechnicianTickets = useMemo(() => {
    const start = (detailPage - 1) * detailPerPage;
    return selectedTechnicianTickets.slice(start, start + detailPerPage);
  }, [selectedTechnicianTickets, detailPage, detailPerPage]);
  useEffect(() => {
    setRankingPage(1);
  }, [query, periodFrom, periodTo, statusFilter, rankingPerPage]);
  useEffect(() => {
    if (rankingPage > totalRankingPages) setRankingPage(totalRankingPages);
  }, [rankingPage, totalRankingPages]);
  useEffect(() => {
    setDetailPage(1);
  }, [selectedTechnicianKey, query, periodFrom, periodTo, statusFilter, detailPerPage]);
  useEffect(() => {
    if (detailPage > totalDetailPages) setDetailPage(totalDetailPages);
  }, [detailPage, totalDetailPages]);

  const handleExportCsv = () => {
    if (performanceRows.length === 0) {
      toast.error('Belum ada data performa untuk diexport');
      return;
    }

    const header = ['Nama Teknisi', 'Approve', 'Selesai', 'Tiket Aktif', 'Butuh Khusus', 'Completion Rate (%)', 'Rata-rata Waktu (jam)', 'Jumlah Pelanggan Ditangani', 'Aktivitas Terakhir'];
    const rows = performanceRows.map((row) => [
      row.technician_name,
      row.approved_count,
      row.completed_count,
      row.active_count,
      row.special_count,
      row.completion_rate,
      row.avg_resolution_hours == null ? '' : row.avg_resolution_hours.toFixed(2),
      row.customer_count,
      formatDateTime(row.last_activity_at),
    ]);

    const csv = [header, ...rows]
      .map((line) => line.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `performa-teknisi-${toDateString(periodFrom)}-${toDateString(periodTo)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const handleMarkAllNotificationsRead = async () => {
    setMarkingNotifications(true);
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { headers: getHeaders() });
      setNotifications((prev) => prev.map((item) => ({ ...item, is_read: true })));
      toast.success('Semua notifikasi ditandai dibaca');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menandai notifikasi');
    } finally {
      setMarkingNotifications(false);
    }
  };

  const handleLogout = async () => {
    await requestStaffLogout();
    clearStaffSession();
    navigate('/dadu/login');
  };

  const roleLabel = role === 'investor' ? 'Investor' : role === 'technician' ? 'Teknisi' : 'Admin';
  const backRoute = getStaffHomeRoute(role);
  const unreadNotifications = notifications.filter((item) => !item.is_read).length;

  return (
    <AppShell>
      <PageContainer className="space-y-4 cc-legacy-surface">
        <StaffPageHeader
          eyebrow="Performa"
          title="Performa Teknisi"
          subtitle={`Analitik penyelesaian tiket lapangan, audit aktivitas, dan notifikasi operasional. Akses: ${roleLabel}.`}
          actions={(
            <>
              <Button variant="outline" className="btn-secondary" onClick={() => navigate(backRoute)}>
                <Home className="w-4 h-4 mr-2" />Kembali
              </Button>
              <Button variant="outline" className="btn-secondary" onClick={() => fetchData(true)} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
              </Button>
              <Button variant="outline" className="btn-secondary" onClick={handleExportCsv}>
                <Download className="w-4 h-4 mr-2" />Export CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout} className="text-red-400 border-red-400/30 hover:bg-red-500/10">
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          )}
        />

        <div className="bg-[#121212] rounded-xl p-4 border border-white/10">
          <div className="grid md:grid-cols-5 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Cari pelanggan / teknisi / catatan tiket" className="pl-9 bg-[#0A0A0A] border-white/10 text-white" />
            </div>
            <div>
              <p className="staff-form-label-xs flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />Dari</p>
              <Input type="date" value={periodFrom} onChange={(event) => setPeriodFrom(event.target.value)} className="bg-[#0A0A0A] border-white/10 text-white" />
            </div>
            <div>
              <p className="staff-form-label-xs">Sampai</p>
              <Input type="date" value={periodTo} onChange={(event) => setPeriodTo(event.target.value)} className="bg-[#0A0A0A] border-white/10 text-white" />
            </div>
            <div>
              <p className="staff-form-label-xs">Status</p>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white"><SelectValue placeholder="Filter status" /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="all" className="text-white">Semua</SelectItem>
                  <SelectItem value="open" className="text-white">Tiket Berjalan</SelectItem>
                  <SelectItem value="done" className="text-white">Tiket Selesai</SelectItem>
                  <SelectItem value="special" className="text-white">Butuh Khusus</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
            <p className="text-gray-500">Update terakhir: {lastUpdated ? new Date(lastUpdated).toLocaleString('id-ID') : '-'}</p>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setAutoRefresh((prev) => !prev)} className={`px-2 py-1 rounded border ${autoRefresh ? 'border-green-500/40 text-green-300 bg-green-500/10' : 'border-white/15 text-gray-300 bg-[#0A0A0A]'}`}>
                Auto refresh 30 detik: {autoRefresh ? 'ON' : 'OFF'}
              </button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 border-[#cbd5e1] bg-white px-3 text-xs text-slate-900 hover:bg-slate-50"
                onClick={() => {
                  setQuery('');
                  setPeriodFrom('');
                  setPeriodTo('');
                  setStatusFilter('all');
                  setRankingPage(1);
                  setDetailPage(1);
                }}
              >
                Reset Filter
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-[#121212] rounded-xl border border-white/10 p-4"><p className="text-gray-400 text-xs">Total Tiket</p><p className="text-white text-xl font-bold">{summary.total_tickets}</p></div>
          <div className="bg-[#121212] rounded-xl border border-white/10 p-4"><p className="text-gray-400 text-xs">Tiket Berjalan</p><p className="text-yellow-300 text-xl font-bold">{summary.open_tickets}</p></div>
          <div className="bg-[#121212] rounded-xl border border-white/10 p-4"><p className="text-gray-400 text-xs">Tiket Selesai</p><p className="text-green-300 text-xl font-bold">{summary.done_tickets}</p></div>
          <div className="bg-[#121212] rounded-xl border border-white/10 p-4"><p className="text-gray-400 text-xs">Butuh Khusus</p><p className="text-red-300 text-xl font-bold">{summary.special_tickets}</p></div>
          <div className="bg-[#121212] rounded-xl border border-white/10 p-4"><p className="text-gray-400 text-xs">Teknisi Aktif</p><p className="text-cyan-300 text-xl font-bold">{summary.active_technicians}</p></div>
        </div>

        <div className="bg-[#121212] rounded-xl border border-white/10 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <h2 className="text-white font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4" />Ranking Performa Teknisi</h2>
            <p className="text-xs text-gray-500">Klik baris teknisi untuk melihat detail tiket</p>
          </div>
          {!loading && performanceRows.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#dbe7ff] bg-white px-4 py-2 text-xs">
              <p className="text-slate-600">Menampilkan {(rankingPage - 1) * rankingPerPage + 1}-{Math.min(rankingPage * rankingPerPage, performanceRows.length)} dari {performanceRows.length}</p>
              <div className="flex items-center gap-2">
                <select value={String(rankingPerPage)} onChange={(e) => setRankingPerPage(Number(e.target.value))} className="px-2 py-1 bg-white border border-[#cbd5e1] rounded text-slate-900">
                  <option value="10">10 / halaman</option>
                  <option value="20">20 / halaman</option>
                  <option value="50">50 / halaman</option>
                </select>
                <Button type="button" size="sm" variant="outline" className="h-8 border-[#cbd5e1] bg-white px-3 text-xs text-slate-900 hover:bg-slate-50" disabled={rankingPage <= 1} onClick={() => setRankingPage(1)}>First</Button>
                <Button type="button" size="sm" variant="outline" className="btn-secondary" disabled={rankingPage <= 1} onClick={() => setRankingPage((p) => Math.max(1, p - 1))}>Prev</Button>
                <span className="text-slate-700">Hal {rankingPage}/{totalRankingPages}</span>
                <Button type="button" size="sm" variant="outline" className="btn-secondary" disabled={rankingPage >= totalRankingPages} onClick={() => setRankingPage((p) => Math.min(totalRankingPages, p + 1))}>Next</Button>
                <Button type="button" size="sm" variant="outline" className="h-8 border-[#cbd5e1] bg-white px-3 text-xs text-slate-900 hover:bg-slate-50" disabled={rankingPage >= totalRankingPages} onClick={() => setRankingPage(totalRankingPages)}>Last</Button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="p-8 text-center text-gray-400">Memuat data performa...</div>
          ) : performanceRows.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Belum ada data performa pada filter ini.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px]">
                <thead className="bg-[#0A0A0A] border-b border-white/10">
                  <tr>
                    <th className="sticky top-0 z-10 bg-[#0A0A0A] text-left py-3 px-4 text-gray-400 text-xs">#</th>
                    <th className="sticky top-0 z-10 bg-[#0A0A0A] text-left py-3 px-4 text-gray-400 text-xs">Teknisi</th>
                    <th className="sticky top-0 z-10 bg-[#0A0A0A] text-left py-3 px-4 text-gray-400 text-xs">Approve</th>
                    <th className="sticky top-0 z-10 bg-[#0A0A0A] text-left py-3 px-4 text-gray-400 text-xs">Selesai</th>
                    <th className="sticky top-0 z-10 bg-[#0A0A0A] text-left py-3 px-4 text-gray-400 text-xs">Aktif</th>
                    <th className="sticky top-0 z-10 bg-[#0A0A0A] text-left py-3 px-4 text-gray-400 text-xs">Butuh Khusus</th>
                    <th className="sticky top-0 z-10 bg-[#0A0A0A] text-left py-3 px-4 text-gray-400 text-xs">Completion</th>
                    <th className="sticky top-0 z-10 bg-[#0A0A0A] text-left py-3 px-4 text-gray-400 text-xs">Rata-rata Waktu</th>
                    <th className="sticky top-0 z-10 bg-[#0A0A0A] text-left py-3 px-4 text-gray-400 text-xs">Pelanggan Ditangani</th>
                    <th className="sticky top-0 z-10 bg-[#0A0A0A] text-left py-3 px-4 text-gray-400 text-xs">Aktivitas Terakhir</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedPerformanceRows.map((row, index) => {
                    const selected = row.key === selectedTechnicianKey;
                    return (
                      <tr key={row.key} className={`border-b border-white/5 cursor-pointer ${selected ? 'bg-cyan-500/10' : 'hover:bg-white/5'}`} onClick={() => setSelectedTechnicianKey(row.key)}>
                        <td className="py-3 px-4 text-gray-300 text-sm">{(rankingPage - 1) * rankingPerPage + index + 1}</td>
                        <td className="py-3 px-4"><p className="text-white text-sm font-semibold">{row.technician_name}</p><p className="text-gray-500 text-xs">ID: {row.technician_id || '-'}</p></td>
                        <td className="py-3 px-4 text-cyan-300 text-sm font-semibold">{row.approved_count}</td>
                        <td className="py-3 px-4 text-green-300 text-sm font-semibold">{row.completed_count}</td>
                        <td className="py-3 px-4 text-yellow-300 text-sm font-semibold">{row.active_count}</td>
                        <td className="py-3 px-4 text-red-300 text-sm font-semibold">{row.special_count}</td>
                        <td className="py-3 px-4"><span className={`text-xs px-2 py-1 rounded border ${row.completion_rate >= 80 ? 'text-green-300 border-green-500/30 bg-green-500/10' : row.completion_rate >= 60 ? 'text-yellow-300 border-yellow-500/30 bg-yellow-500/10' : 'text-red-300 border-red-500/30 bg-red-500/10'}`}>{row.completion_rate}%</span></td>
                        <td className="py-3 px-4 text-gray-300 text-sm">{row.avg_resolution_hours == null ? '-' : `${formatNumber(row.avg_resolution_hours, 2)} jam`}</td>
                        <td className="py-3 px-4 text-gray-300 text-sm">{row.customer_count}</td>
                        <td className="py-3 px-4 text-gray-400 text-xs">{formatDateTime(row.last_activity_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="grid xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 bg-[#121212] rounded-xl border border-white/10 overflow-hidden">
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <div>
                <h2 className="text-white font-semibold flex items-center gap-2"><ClipboardList className="w-4 h-4" />Detail Teknisi</h2>
                <p className="text-xs text-gray-500">{selectedTechnician ? selectedTechnician.technician_name : 'Belum ada teknisi dipilih'}</p>
              </div>
              {selectedTechnician && <span className="text-xs px-2 py-1 rounded border border-cyan-500/30 bg-cyan-500/10 text-cyan-300">{selectedTechnicianTickets.length} tiket terkait</span>}
            </div>

            {!selectedTechnician ? (
              <div className="p-8 text-center text-gray-500">Pilih teknisi pada tabel di atas untuk melihat detail pekerjaan.</div>
            ) : (
              <div className="p-4 space-y-4">
                <div className="grid sm:grid-cols-4 gap-3">
                  <div className="rounded-lg bg-[#0A0A0A] border border-white/10 p-3"><p className="text-gray-500 text-xs">Approve</p><p className="text-cyan-300 text-xl font-bold">{selectedTechnician.approved_count}</p></div>
                  <div className="rounded-lg bg-[#0A0A0A] border border-white/10 p-3"><p className="text-gray-500 text-xs">Selesai</p><p className="text-green-300 text-xl font-bold">{selectedTechnician.completed_count}</p></div>
                  <div className="rounded-lg bg-[#0A0A0A] border border-white/10 p-3"><p className="text-gray-500 text-xs">Rata-rata Waktu</p><p className="text-white text-xl font-bold">{selectedTechnician.avg_resolution_hours == null ? '-' : `${formatNumber(selectedTechnician.avg_resolution_hours, 1)}j`}</p></div>
                  <div className="rounded-lg bg-[#0A0A0A] border border-white/10 p-3"><p className="text-gray-500 text-xs">Aktivitas Terakhir</p><p className="text-white text-sm font-semibold">{formatDateTime(selectedTechnician.last_activity_at)}</p></div>
                </div>

                <div className="space-y-3 max-h-[460px] overflow-auto pr-1">
                  {selectedTechnicianTickets.length > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#dbe7ff] bg-white px-3 py-2 text-xs">
                      <p className="text-slate-600">Menampilkan {(detailPage - 1) * detailPerPage + 1}-{Math.min(detailPage * detailPerPage, selectedTechnicianTickets.length)} dari {selectedTechnicianTickets.length}</p>
                      <div className="flex items-center gap-2">
                        <select value={String(detailPerPage)} onChange={(e) => setDetailPerPage(Number(e.target.value))} className="px-2 py-1 bg-white border border-[#cbd5e1] rounded text-slate-900">
                          <option value="8">8 / halaman</option>
                          <option value="12">12 / halaman</option>
                          <option value="20">20 / halaman</option>
                        </select>
                        <Button type="button" size="sm" variant="outline" className="h-8 border-[#cbd5e1] bg-white px-3 text-xs text-slate-900 hover:bg-slate-50" disabled={detailPage <= 1} onClick={() => setDetailPage(1)}>First</Button>
                        <Button type="button" size="sm" variant="outline" className="btn-secondary" disabled={detailPage <= 1} onClick={() => setDetailPage((p) => Math.max(1, p - 1))}>Prev</Button>
                        <span className="text-slate-700">Hal {detailPage}/{totalDetailPages}</span>
                        <Button type="button" size="sm" variant="outline" className="btn-secondary" disabled={detailPage >= totalDetailPages} onClick={() => setDetailPage((p) => Math.min(totalDetailPages, p + 1))}>Next</Button>
                        <Button type="button" size="sm" variant="outline" className="h-8 border-[#cbd5e1] bg-white px-3 text-xs text-slate-900 hover:bg-slate-50" disabled={detailPage >= totalDetailPages} onClick={() => setDetailPage(totalDetailPages)}>Last</Button>
                      </div>
                    </div>
                  )}
                  {selectedTechnicianTickets.length === 0 ? (
                    <p className="text-gray-500 text-sm">Belum ada tiket terkait pada filter ini.</p>
                  ) : pagedSelectedTechnicianTickets.map((ticket) => (
                    <div key={ticket.id} className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4 space-y-2">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                        <div>
                          <p className="text-white font-semibold">{ticket.customer_name || '-'}</p>
                          <p className="text-xs text-gray-500">{ticket.customer_code || '-'} | {ticket.customer_phone || '-'}</p>
                        </div>
                        <span className={`text-[11px] px-2 py-1 rounded border ${getTicketStatusClass(ticket.status)}`}>{getTicketStatusLabel(ticket.status)}</span>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-2 text-xs text-gray-400">
                        <p>Dibuat: {formatDateTime(ticket.created_at)}</p>
                        <p>Selesai: {formatDateTime(ticket.completed_at)}</p>
                        <p>Approve: {(ticket.approved_technician_names || []).join(', ') || '-'}</p>
                        <p>Penyelesai: {(ticket.completed_technician_names || []).join(', ') || '-'}</p>
                      </div>
                      {ticket.issue_notes && <p className="text-sm text-gray-300">Keluhan: {ticket.issue_notes}</p>}
                      {ticket.completion_notes && <p className="text-sm text-gray-400">Catatan: {ticket.completion_notes}</p>}
                      {Array.isArray(ticket.evidence) && ticket.evidence.length > 0 && (
                        <div className="pt-1">
                          <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">Bukti pekerjaan</p>
                          <div className="flex flex-wrap gap-2">
                            {ticket.evidence.slice(0, 4).map((item) => (
                              <a key={item.id || item.url} href={`${BASE_URL}${item.url}`} target="_blank" rel="noreferrer" className="block h-16 w-16 overflow-hidden rounded border border-white/10 bg-[#050505]">
                                <img src={`${BASE_URL}${item.url}`} alt={item.original_name || 'Bukti pekerjaan'} className="h-full w-full object-cover" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="bg-[#121212] rounded-xl border border-white/10 overflow-hidden">
              <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                <div>
                  <h2 className="text-white font-semibold flex items-center gap-2"><Bell className="w-4 h-4" />Notifikasi Internal</h2>
                  <p className="text-xs text-gray-500">{unreadNotifications} belum dibaca</p>
                </div>
                <Button variant="outline" size="sm" className="btn-secondary" onClick={handleMarkAllNotificationsRead} disabled={markingNotifications || notifications.length === 0}>{markingNotifications ? 'Memproses...' : 'Tandai Semua'}</Button>
              </div>
              <div className="p-4 space-y-2 max-h-[260px] overflow-auto pr-1">
                {notifications.length === 0 ? <p className="text-gray-500 text-sm">Belum ada notifikasi.</p> : notifications.map((item) => (
                  <div key={item.id} className={`rounded-lg border p-3 ${getNotificationClass(item.level)} ${item.is_read ? 'opacity-70' : ''}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold">{item.title || '-'}</p>
                        <p className="text-xs mt-1 leading-relaxed">{item.message || '-'}</p>
                      </div>
                      {!item.is_read && <span className="h-2.5 w-2.5 rounded-full bg-cyan-300 mt-1" />}
                    </div>
                    <p className="text-[11px] mt-2 opacity-80">{formatDateTime(item.created_at)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#121212] rounded-xl border border-white/10 overflow-hidden">
              <div className="px-4 py-3 border-b border-white/10"><h2 className="text-white font-semibold flex items-center gap-2"><Wrench className="w-4 h-4" />Audit Aktivitas</h2></div>
              <div className="p-4 space-y-2 max-h-[320px] overflow-auto pr-1">
                {activityLogs.length === 0 ? <p className="text-gray-500 text-sm">Belum ada activity log.</p> : activityLogs.map((log) => (
                  <div key={log.id} className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm text-white font-semibold">{log.summary || '-'}</p>
                      <span className="text-[10px] px-2 py-1 rounded border border-white/10 text-gray-400 uppercase">{log.category || 'general'}</span>
                    </div>
                    <p className="text-xs text-gray-400">{log.actor_name || 'Sistem'} • {log.actor_role || '-'}</p>
                    <p className="text-[11px] text-gray-500">{formatDateTime(log.created_at)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[#121212] rounded-xl border border-white/10 p-4 text-xs text-gray-400">
          <p className="flex items-center gap-2 text-gray-300 font-semibold mb-2"><Wrench className="w-4 h-4" />Catatan Perhitungan</p>
          <p>Completion dihitung dari rasio tiket selesai dibanding jumlah approval yang diambil teknisi pada periode filter.</p>
          <p>Rata-rata waktu dihitung dari selisih `created_at` ke `completed_at` pada tiket yang diselesaikan.</p>
        </div>
      </PageContainer>
    </AppShell>
  );
};

export default TechnicianPerformancePage;





